/** Roster shift times are stored as 24h `HH:mm`; UI uses 12h clock with quarter hours only. */

export const ROSTER_QUARTER_MINUTES = [0, 15, 30, 45] as const;

export type Meridiem = 'am' | 'pm';

export function snapMinuteToQuarter(minute: number): number {
  let best: number = ROSTER_QUARTER_MINUTES[0];
  let bestDist = Infinity;
  for (const q of ROSTER_QUARTER_MINUTES) {
    const d = Math.abs(minute - q);
    if (d < bestDist) {
      bestDist = d;
      best = q;
    }
  }
  return best;
}

function clampHour24(h: number): number {
  if (!Number.isFinite(h)) return 0;
  return Math.max(0, Math.min(23, Math.floor(h)));
}

function clampMinute(m: number): number {
  if (!Number.isFinite(m)) return 0;
  return Math.max(0, Math.min(59, Math.floor(m)));
}

/** Parse `HH:mm` (24h); snaps minutes to nearest quarter for display/editing. */
export function hhmm24ToParts(value: string): { hour12: number; minute: number; meridiem: Meridiem } {
  const [hs, ms] = value.split(':');
  const h24 = clampHour24(parseInt(hs ?? '0', 10));
  const rawM = clampMinute(parseInt(ms ?? '0', 10));
  const minute = snapMinuteToQuarter(rawM);
  let hour12: number;
  let meridiem: Meridiem;
  if (h24 === 0) {
    hour12 = 12;
    meridiem = 'am';
  } else if (h24 < 12) {
    hour12 = h24;
    meridiem = 'am';
  } else if (h24 === 12) {
    hour12 = 12;
    meridiem = 'pm';
  } else {
    hour12 = h24 - 12;
    meridiem = 'pm';
  }
  return { hour12, minute, meridiem };
}

/** Build 24h `HH:mm` from 12h clock + quarter minute. */
export function partsToHhmm24(hour12: number, minute: number, meridiem: Meridiem): string {
  const h12 = Math.max(1, Math.min(12, Math.floor(hour12)));
  const m = ROSTER_QUARTER_MINUTES.includes(minute as (typeof ROSTER_QUARTER_MINUTES)[number])
    ? minute
    : snapMinuteToQuarter(minute);
  let h24: number;
  if (meridiem === 'am') {
    h24 = h12 === 12 ? 0 : h12;
  } else {
    h24 = h12 === 12 ? 12 : h12 + 12;
  }
  return `${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
